import React, { useEffect, useState } from 'react';
import { Activity, Share, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import useIsMobile from '../../utils/useIsMobile';

// "Add to home screen" card, phones only.
//
// Chromium fires `beforeinstallprompt` and lets us trigger the real install
// dialog. iOS fires nothing and has no API, so there the card just points at
// the Share sheet. Either way it is dismissible and stays gone for a month —
// this is a working tool, not a place to nag people.

const DISMISS_KEY = 'ld:install-dismissed-at';
const DISMISS_DAYS = 30;
const STROKE = 2.75; // Organic's lucide weight

function dismissedRecently() {
  try {
    const at = Number(window.localStorage.getItem(DISMISS_KEY));
    if (!at) return false;
    return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch (err) {
    return false; // Safari private mode throws on localStorage
  }
}

function rememberDismissal() {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch (err) {
    /* nothing to do — the card simply comes back next session */
  }
}

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function InstallPrompt() {
  const { isAuthenticated } = useAuth();
  const isMobile = useIsMobile();
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(() => dismissedRecently());
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    const onBeforeInstall = (event) => {
      // Suppress Chrome's own mini-infobar so the card is the only ask.
      event.preventDefault();
      setDeferred(event);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const iosHint = isIos() && !deferred;
  if (!isAuthenticated || !isMobile || installed || dismissed) return null;
  if (!deferred && !iosHint) return null;

  const dismiss = () => {
    rememberDismissal();
    setDismissed(true);
  };

  const install = async () => {
    if (!deferred) return;
    setDeferred(null);
    deferred.prompt();
    const choice = await deferred.userChoice;
    // A decline is an answer: don't re-ask on the next screen.
    if (choice && choice.outcome !== 'accepted') dismiss();
  };

  return (
    <div className="pwa-card" role="dialog" aria-label="Install Lead Discovery">
      <span className="pwa-card-mark">
        <Activity size={18} strokeWidth={STROKE} />
      </span>
      <div className="pwa-card-text">
        <div className="pwa-card-title">Install Lead Discovery</div>
        <div className="pwa-card-sub">
          {iosHint ? (
            <>
              Tap <Share size={12} strokeWidth={STROKE} style={{ verticalAlign: '-2px' }} /> then
              “Add to Home Screen”.
            </>
          ) : (
            'Full screen, straight from your home screen.'
          )}
        </div>
      </div>
      <div className="pwa-card-actions">
        {!iosHint && (
          <button type="button" className="pwa-install-btn" onClick={install}>
            Install
          </button>
        )}
        <button type="button" className="pwa-dismiss" aria-label="Dismiss" onClick={dismiss}>
          <X size={16} strokeWidth={STROKE} />
        </button>
      </div>
    </div>
  );
}

export default InstallPrompt;
